import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'
import { catchError } from 'rxjs/operators'
import { Subscription } from 'rxjs/Subscription'
import { Subject } from 'rxjs/Subject'
import { Socket } from 'phoenix-socket'
import { captureException } from 'utils/SentryHelpers'
import * as GatesSelectors from 'app/modules/gates/selectors'
import * as ConversationActions from 'app/modules/conversation/actions'
import * as MessageActions from 'app/modules/message/actions'
import * as actions from './actions'
import * as constants from './constants'
import * as selectors from './selectors'
import * as api from './api'

const AUTO_JOIN_CHANNELS = {
  [constants.SocketCluster.MESSAGES]: [constants.SocketChannel.ORG_PRIVATE, constants.SocketChannel.USER],
  [constants.SocketCluster.PUSH]: [constants.SocketChannel.ORG_PRIVATE],
}

const makeClusterEpic = (clusterName) => {
  const authenticateEpic = (action$, store) =>
    action$
      .ofType(constants.AUTHENTICATE)
      .pluck('payload')
      .filter((payload) => payload.clusterName === clusterName)
      .map(() => {
        const state = store.getState()
        return {
          apiBaseUrl: selectors.getApiBaseUrl(state, { clusterName }),
          authParams: selectors.getAuthParams(state, { clusterName }),
        }
      })
      .switchMap((payload) => {
        return api.authenticate(payload)
          .map((data) => actions.authenticateSuccess({ clusterName, ...data }))
          .startWith(actions.authenticatePending({ clusterName }))
          .catch((error) => {
            captureException(error, { extra: { clusterName } })
            return Observable.of(actions.authenticateFailure({ clusterName, error }))
          })
      })

  const connectEpic = (action$, store) =>
    action$
      .ofType(constants.AUTHENTICATE_SUCCESS)
      .pluck('payload')
      .filter((payload) => payload.clusterName === clusterName)
      .switchMap(({ sessionToken }) => {
        const joinChannel$ =
          action$
            .ofType(constants.JOIN_CHANNEL)
            .pluck('payload')
            .filter((payload) => payload.clusterName === clusterName)

        return Observable.create((observer) => {
          const socket = new Socket(`${selectors.getWssBaseUrl(store.getState(), { clusterName })}/ws`, {
            params: { session_token: sessionToken },
            /**
             * If a Socket receives and error (onError) or becomes disconnected
             * (onClose), it will automatically attempt to reconnect after the
             * timeout returned from this function.
             *
             * If a Channel receives an error (onError) or could not be joined
             * (join().receive('error' | 'timeout')), it will automatically
             * attempt to rejoin after the timeout returned from this function.
             *
             * The `attempt` argument is incremented by 1 each time it is called
             * until successful reconnect or rejoin.
             *
             * We dispatch an action to keep track of when reconnect/rejoin
             * attempts have been queued so that we may display a countdown
             * timer to the user.
             */
            reconnectAfterMs: (attempt) => {
              const timeUntilReconnect = [1000, 2000, 5000, 10000][attempt - 1] || 10000
              observer.next(actions.socketReconnectAttemptQueued({ clusterName, timeUntilReconnect }))
              return timeUntilReconnect
            },
          })

          let awaitingInitialConnection = true

          socket.onOpen(() => {
            observer.next(actions.socketOpenEventReceived({ clusterName }))

            if (!awaitingInitialConnection) {
              const channelsToJoin = selectors.channelsToJoin(store.getState(), { clusterName })
              channelsToJoin.forEach((channel) => {
                channel.rejoinUntilConnected()
              })
            }

            awaitingInitialConnection = false
          })

          socket.onError(() => {
            observer.next(actions.socketErrorEventReceived({ clusterName }))
          })

          socket.onClose(() => {
            observer.next(actions.socketCloseEventReceived({ clusterName }))
          })

          const subscription = new Subscription()

          subscription.add(joinChannel$.subscribe({
            next: ({ channelName }) => {
              const channel = socket.channel(selectors.getChannelName(store.getState(), { clusterName, channelName }))

              const onMessageSubject = new Subject()

              channel.onError(() => {
                observer.next(actions.channelErrorEventReceived({ clusterName, channelName }))
              })

              channel.onClose(() => {
                observer.next(actions.channelCloseEventReceived({ clusterName, channelName }))
              })

              channel.onMessage = (event, payload, ref) => {
                onMessageSubject.next({
                  event,
                  payload,
                  ref,
                })
                return payload
              }

              const leaveChannel$ = action$
                .ofType(constants.LEAVE_CHANNEL)
                .filter(({ payload }) => payload.clusterName === clusterName)
                .filter(({ payload }) => payload.channelName === channelName)
                .map(() => channel.leave())

              observer.next(actions.joinChannelPending({
                clusterName,
                channelName,
                channel,
                onMessageSubject,
                leaveChannel$,
              }))

              channel
                .join()
                .receive('ok', () => {
                  observer.next(actions.joinChannelSuccess({
                    clusterName,
                    channelName,
                    channel,
                    leaveChannel$,
                  }))
                })
                .receive('error', (error) => {
                  observer.next(actions.joinChannelFailure({
                    clusterName,
                    channelName,
                    channel,
                    error,
                  }))
                })
                .receive('timeout', () => {
                  observer.next(actions.joinChannelFailure({
                    clusterName,
                    channelName,
                    channel,
                    error: 'The request has timed out. please try again later',
                  }))
                })
            },
          }))

          socket.connect()

          observer.next(actions.connect({
            clusterName,
            socket,
          }))

          return () => {
            socket.disconnect(() => {
              subscription.unsubscribe()
            })
          }
        })
          .takeUntil(action$.ofType(constants.DISCONNECT))
      })

  /**
   * Listens for queued Socket reconnect / Channel rejoin attempts.
   * When an attempt is queued, we setup an interval to fire off a
   * `SOCKET_RECONNECT_TIMER_TICKED` action every second. When this action
   * reaches the reducer, the `timeUntilReconnect` value is updated by
   * subtracting 1 second. This allows us to show the user how many seconds are
   * remaining before we automatically attempt to reconnect.
   *
   * Note that the timer will tick every second until a channel is successfully
   * rejoined, closed gracefully, or the socket is opened or disconnected AND
   * there are no remaining channels left to join.
   */
  const socketReconnectTimerEpic = (action$, store) =>
    action$
      .ofType(constants.SOCKET_RECONNECT_ATTEMPT_QUEUED)
      .pluck('payload')
      .filter((payload) => payload.clusterName === clusterName)
      .switchMap((payload) =>
        Observable.interval(1000)
          .mapTo(actions.socketReconnectTimerTicked({
            clusterName: payload.clusterName,
            timePassed: 1000,
          }))
          .takeUntil(
            Observable.merge(
              action$
                .ofType(
                  constants.SOCKET_OPEN_EVENT_RECEIVED,
                  constants.JOIN_CHANNEL_SUCCESS,
                  constants.CHANNEL_CLOSE_EVENT_RECEIVED,
                  constants.DISCONNECT,
                )
                .pluck('payload')
                .filter((payload) => payload.clusterName === clusterName)
                .filter(() => !selectors.channelsToJoin(store.getState(), { clusterName }).count()),
              Observable.timer(payload.timeUntilReconnect),
            )
          )
      )

  const joinChannelEpic = (action$, store) =>
    action$
      .ofType(constants.CONNECT)
      .pluck('payload')
      .filter(() => !(GatesSelectors.hasSplitEndUserChannel(store.getState()) && clusterName === constants.SocketCluster.PUSH))
      .filter((payload) => payload.clusterName === clusterName)
      .flatMap(() => {
        return AUTO_JOIN_CHANNELS[clusterName]
          .map((channelName) => actions.joinChannel({ clusterName, channelName }))
      })

  return combineEpics(
    authenticateEpic,
    connectEpic,
    socketReconnectTimerEpic,
    joinChannelEpic,
  )
}

export default combineEpics(
  makeClusterEpic(constants.SocketCluster.MESSAGES),
  makeClusterEpic(constants.SocketCluster.PUSH)
)



// WEBPACK FOOTER //
// ./src/scripts/app/modules/socket/epic.js